import { Colour } from '../api/jerseyApi';

// Form data interfaces
export interface ColorFormData {
  name: string;
  hexadecimal: string;
  cmyk?: string;
  rgb?: string;
}

// Filter and search interfaces
export interface ColorFilters {
  search: string;
}

export interface ColorListState {
  colors: Colour[];
  loading: boolean;
  error: string | null;
  filters: ColorFilters;
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

// Component props interfaces
export interface ColorCardProps {
  color: Colour;
  onEdit: (color: Colour) => void;
  onDelete: (color: Colour) => void;
}

export interface ColorFormProps {
  color?: Colour;
  onSubmit: (data: ColorFormData) => void;
  onCancel: () => void;
  loading?: boolean;
}

export interface ColorPreviewProps {
  color: Colour;
  size?: 'small' | 'medium' | 'large';
  showName?: boolean;
  showHex?: boolean;
}

// Action types for color management
export type ColorAction = 
  | 'create'
  | 'edit'
  | 'delete'
  | 'view';

export interface ColorActionData {
  action: ColorAction;
  color?: Colour;
}

// Validation interfaces
export interface ColorValidationErrors {
  name?: string;
  hexadecimal?: string;
  cmyk?: string;
  rgb?: string;
}

export interface ColorFormValidation {
  isValid: boolean;
  errors: ColorValidationErrors;
}

// Color format conversion utilities
export interface RGBColor {
  r: number;
  g: number;
  b: number;
}

export interface CMYKColor {
  c: number;
  m: number;
  y: number;
  k: number;
}

export interface HSLColor {
  h: number;
  s: number;
  l: number;
}

// Constants
export const COLOR_CONSTANTS = {
  MAX_NAME_LENGTH: 255,
  HEX_PATTERN: /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/,
  CMYK_PATTERN: /^(\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})$/,
  RGB_PATTERN: /^(\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})$/,
  PREVIEW_SIZES: {
    small: { width: 30, height: 30 },
    medium: { width: 50, height: 50 },
    large: { width: 80, height: 80 },
  },
  DEFAULT_COLORS: [
    '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
    '#000000', '#FFFFFF', '#808080', '#800000', '#008000', '#000080',
    '#808000', '#800080', '#008080', '#C0C0C0', '#FFA500', '#A52A2A',
    '#DDA0DD', '#98FB98', '#F0E68C', '#DEB887', '#5F9EA0', '#FF1493'
  ],
} as const;

// Color utility functions type definitions
export interface ColorUtils {
  hexToRgb: (hex: string) => RGBColor | null;
  rgbToHex: (r: number, g: number, b: number) => string;
  hexToHsl: (hex: string) => HSLColor | null;
  hslToHex: (h: number, s: number, l: number) => string;
  rgbToCmyk: (r: number, g: number, b: number) => CMYKColor;
  cmykToRgb: (c: number, m: number, y: number, k: number) => RGBColor;
  isValidHex: (hex: string) => boolean;
  isValidRgb: (rgb: string) => boolean;
  isValidCmyk: (cmyk: string) => boolean;
  formatRgb: (r: number, g: number, b: number) => string;
  formatCmyk: (c: number, m: number, y: number, k: number) => string;
  parseRgb: (rgb: string) => RGBColor | null;
  parseCmyk: (cmyk: string) => CMYKColor | null;
  getContrastColor: (hex: string) => string;
  getLuminance: (hex: string) => number;
  getColorName: (hex: string) => string;
}

// List parameters for API calls
export interface ColorListParams {
  skip?: number;
  limit?: number;
  search?: string;
}

// Paginated response interface
export interface PaginatedColorResponse {
  items: Colour[];
  total: number;
  page: number;
  size: number;
  pages: number;
}
